// The tank gunner's rangefinder and range marks, shared by the gunsight
// (main.js) and its regression test (client/test/gunsight.test.mjs) so the
// two can never drift.
//
// A land hull's gun fires straight down the sight's lay (fireLandShell in
// internal/game/landcombat.go) and the round drops: nothing lifts the bore
// for range, so a far hull wants the crosshair held over her. Holding over
// is the gunner's craft (flightsim-j9w). Two things here make it a craft
// rather than a guess:
//
//   * the range marks (landRoundAt, holdoverRanges): the ladder the sight
//     hangs under its crosshair, each mark where the round really is when it
//     has flown that far. Put the mark for a hull's range on the hull and the
//     round finds her.
//   * the rangefinder (landSightRange), which tells the gunner that range.
//     Its whole job is making the range the TARGET's:
//
//   * a hull under (or brushing) the crosshair ranges at the hull, at its real
//     height on the steppe. The warship sight this grew out of took every hull
//     to be floating at sea level, so on land it never found one: it read the
//     dirt behind the target instead (a spot hundreds of metres long) or, over
//     a skyline hull, the sky (the gun's full reach).
//   * a building face stops a land round (landShellStep), so it stops the
//     rangefinder too.
//   * the ground: the drawn triangle surface marched at the terrain grid's own
//     stride, and the crossing bisected, so a crest the ray grazes reads true.
//   * nothing at all (the sky): the gun's full reach, flagged MAX. That is the
//     naval "over the horizon" answer, and the missile's guidance point and
//     the artillery plot both want a far point there.
//
// The range also steers the Bradley's missile and plots the artillery, so
// every hull ranges; only the warships' batteries still elevate to it.
//
// The eye is the tank's own sight head, in every theater. Combined arms once
// ranged its tanks from a warship's 34 m masthead: an Abrams laying on a hull
// a kilometre off read three.
//
// The warship's director has its own rangefinder here too (navalSightRange),
// for the Brick Gulf, where a captain lays on tanks as well as ships. It ranged
// every hull at the sea under it, so an Abrams up on the coastal farmland (the
// shore rises 10-20 m within a kilometre of the surf) sat too far above its
// snap for the sight to find it. The sight ran on past the tank and read the
// field behind it, or the sky. And the salvo it laid came down five metres
// over the waterline wherever it was ranged: the ground the tank stood on
// caught every shell short.

import { GRAVITY } from "./fdm.js";
import { LAND_BUILDING_GRID_M } from "./terrain_core.js";

// Sight-head height above a land hull's anchor (the anchor rides 1 m over the
// ground). Mirrors the server's landMountY (2.2 m muzzle, eye a hair above):
// the gunner's eye at the turret, where the gun you see is. It rode a
// mast-high 6.4 m while the ground was 3 m blocks, to see past the first
// uphill lip in front of the hull; the smooth ground has none.
export const LAND_SIGHT_Y = 2.4;

// The chase boom behind a crewed land hull: back along the aim's heading and
// up over the sight head. The camera out here rides 7 m above the gun line,
// which is why it must look THROUGH the ranged point (landChaseLook) rather
// than along the sight at a fixed distance.
export const LAND_CHASE_BACK_M = 16;
export const LAND_CHASE_UP_M = 7;

// Rangefinder stride: the terrain grid itself (12 m). A flat tank shot
// grazes crests a coarser stride steps clean over, and a town house is
// 10-18 m on a side. Six halvings then pin the crossing to under 20 cm.
const MARCH_M = 12;
const BISECT = 6;

// Which hull the sight ranges. A hull the ray passes THROUGH is in the way:
// the round meets it, so it wins, nearest first. "Through" means the hull's
// own shape (an ellipsoid on its hit box, laid along its heading). A platoon
// mate parked three metres abeam of the line is beside it, not in it: the
// round passes it by. Failing that, the sight snaps to a hull the ray
// passes NEAR: within the sphere around its box plus a slack that grows with
// range, 4 mrad floored at a metre (a hand's width over the roof through the
// Abrams' 6° optic). So a crosshair resting just above a skyline turret
// still reads the turret's range, and the round crosses the sight line
// THERE, a near miss over the roof instead of six metres up at the gun's full
// reach. Among near hulls the one closest to the crosshair in ANGLE wins: a
// second hull brushing the ray a few degrees off, nearer the gun, must not
// steal the range from the one the gunner is laying on.
const SNAP_FLOOR_M = 1.0;
const SNAP_MRAD = 0.004;

/**
 * An enemy hull as the rangefinder sees it, written into `out`: the centre
 * of its hit box, the box's half-extents along its heading, across it, and
 * up it (a, b, c), its heading's unit vector (fx, fz), and the radius r of
 * the sphere around it. A land hull stands on the ground: its box runs from
 * the dirt (anchor - 1 m) to the roof (turret height + 1.3 m, never under
 * 2.4 m) and is as long and broad as the server's strike box (landHullHit
 * and landVehicleTopY in landcombat.go). A warship in the Gulf is taken the
 * way the fleet's own sight takes her: a sphere at her waterline with the
 * naval sight's generous reach, flagged out.naval.
 *
 * @param x,y,z   the hull's rendered anchor (world units)
 * @param yaw     its heading, yaw = atan2(-z, x)
 * @param type    its groundVehicles.types entry
 * @param waterLevelM  the sea surface
 */
export function sightHull(x, y, z, yaw, type, waterLevelM, out = {}) {
  out.x = x;
  out.z = z;
  out.fx = Math.cos(yaw);
  out.fz = -Math.sin(yaw);
  out.naval = !!type?.naval;
  if (out.naval) {
    const r = (type.hullHalfLenM > 0 ? type.hullHalfLenM : 40) + 15;
    out.y = waterLevelM;
    out.a = out.b = out.c = out.r = r;
    return out;
  }
  const top = Math.max((type?.turretYM ?? 0) + 1.3, 2.4);
  const hit = type?.hitRadiusM > 0 ? type.hitRadiusM : 3.5;
  out.a = type?.hullHalfLenM > 0 ? type.hullHalfLenM : hit;
  out.b = type?.hullHalfBeamM > 0 ? type.hullHalfBeamM : hit * 0.5;
  out.c = top / 2;
  out.y = y - 1 + out.c;
  out.r = Math.hypot(out.a, out.c);
  return out;
}

// Does the ray (eye + d·t, t > 0) pass through the hull's ellipsoid? Scale
// the hull's frame so the ellipsoid is the unit sphere and ask whether the
// ray's closest approach to its centre lies inside it.
function pierces(h, eye, dx, dy, dz) {
  const ex = eye.x - h.x;
  const ey = eye.y - h.y;
  const ez = eye.z - h.z;
  // Hull frame: along its heading (fx, fz), across it (-fz, fx), and up.
  const ef = (ex * h.fx + ez * h.fz) / h.a;
  const es = (ez * h.fx - ex * h.fz) / h.b;
  const eu = ey / h.c;
  const df = (dx * h.fx + dz * h.fz) / h.a;
  const ds = (dz * h.fx - dx * h.fz) / h.b;
  const du = dy / h.c;
  const dd = df * df + ds * ds + du * du;
  const t = -(ef * df + es * ds + eu * du) / dd;
  if (t <= 0) return false;
  const mf = ef + df * t;
  const ms = es + ds * t;
  const mu = eu + du * t;
  return mf * mf + ms * ms + mu * mu <= 1;
}

/**
 * Range to what a land gunsight's ray meets first. Returns {range, max}: the
 * HORIZONTAL range in metres (what the server's elevationForRange wants) and
 * whether it is the MAX fallback (nothing under the crosshair within reach).
 *
 * @param o.eye    the sight head {x, y, z}
 * @param o.yaw    sight bearing, yaw = atan2(-z, x)
 * @param o.pitch  sight elevation (rad)
 * @param o.maxR   the hull's ranging reach (main gun, missile, fire mission)
 * @param o.field  the terrain field (surfaceAt, landBuildingAt)
 * @param o.hulls  enemy hulls from sightHull(); o.nHulls of them are live
 *                 (so the caller can reuse one array frame to frame)
 * @param o.peakY  nothing stands above this: a climbing ray past it is sky
 */
export function landSightRange(o) {
  const { eye, field } = o;
  const maxR = o.maxR ?? 0;
  const ch = Math.cos(o.pitch);
  const dx = Math.cos(o.yaw) * ch;
  const dy = Math.sin(o.pitch);
  const dz = -Math.sin(o.yaw) * ch;

  let tHit = Infinity;
  const hulls = o.hulls ?? [];
  const n = o.nHulls ?? hulls.length;
  let best = Infinity;
  for (let i = 0; i < n; i++) {
    const h = hulls[i];
    const px = h.x - eye.x;
    const py = h.y - eye.y;
    const pz = h.z - eye.z;
    const t = px * dx + py * dy + pz * dz; // along the ray to the closest approach
    if (t <= 0) continue;
    const ex = px - dx * t;
    const ey = py - dy * t;
    const ez = pz - dz * t;
    const miss = Math.sqrt(ex * ex + ey * ey + ez * ez);
    if (miss > h.r + Math.max(SNAP_FLOOR_M, SNAP_MRAD * t)) continue;
    // In the way scores zero (nearest wins); a near hull scores its angle.
    const score = pierces(h, eye, dx, dy, dz) ? 0 : miss / t;
    if (score < best || (score === best && t < tHit)) {
      best = score;
      tHit = t;
    }
  }

  // The ground, the sea (surfaceAt never dips under the water), and the town:
  // whatever the ray meets short of the ranged hull, or of the reach.
  const blocked = (d) => {
    const x = eye.x + dx * d;
    const z = eye.z + dz * d;
    const y = eye.y + dy * d;
    const s = field.surfaceAt(x, z);
    if (s > y) return true;
    const b = field.landBuildingAt ? field.landBuildingAt(x, z) : null;
    return !!b && y <= s + b.h;
  };
  const tCap = maxR > 0 ? maxR / Math.max(ch, 0.5) : 14000;
  const tLim = Math.min(tHit, tCap);
  const peak = o.peakY ?? Infinity;
  let prev = 0;
  for (let d = MARCH_M; d < tLim; d += MARCH_M) {
    if (dy >= 0 && eye.y + dy * d > peak) break; // climbed over everything: sky
    if (blocked(d)) {
      let lo = prev;
      let hi = d;
      for (let k = 0; k < BISECT; k++) {
        const mid = 0.5 * (lo + hi);
        if (blocked(mid)) hi = mid;
        else lo = mid;
      }
      tHit = hi;
      break;
    }
    prev = d;
  }

  let range = tHit * ch;
  let max = false;
  if (maxR > 0 && range >= maxR - 2) {
    max = true;
    range = Math.min(range, maxR);
  }
  return { range: Number.isFinite(range) ? range : 0, max };
}

// The warship director's terrain march, coarse on purpose and the fleet
// sight's own since the naval theater shipped: the first sample 250 m out,
// then a 90 m stride. That is fine enough to find an island that would break
// the salvo, and cheap enough to run every frame out to 13 km.
const NAVAL_MARCH_START_M = 250;
const NAVAL_MARCH_M = 90;

/**
 * Range to what a warship director's ray meets first: the fleet's twin of
 * landSightRange. Returns {range, max, y}: the HORIZONTAL range (what the
 * battery's auto-elevation wants), whether it is the MAX fallback, and the
 * height of the point ranged. That height is where the salvo should come
 * down: the sea for a ship or open water, a tank's hit box up on the shore,
 * the ground where the sight meets a hillside. At MAX it is the sea: the
 * battery throws as far as it can.
 *
 * Each kind of hull is ranged the way its own crews range it:
 *   * a warship by the fleet sight's snap, exactly as it always was: the
 *     nearest one whose waterline sphere (her length plus 15 m) the ray
 *     passes through, so a crosshair anywhere on her reads one steady range.
 *   * a land hull at its real height, by the tank sight's rule: in the way
 *     wins, nearest first, else the hull nearest the crosshair in angle.
 *     Taken at the sea under it, an Abrams on a 20 m bluff stood 20 m above
 *     its own 19 m snap, and the sight never found it.
 * A ship the ray reaches scores as a hull in the way, so a fight at sea,
 * where every hull is a ship, picks exactly the hull it always picked.
 *
 * Then the sea plane, and the terrain march out to the ranged hull or the
 * reach, the way the fleet sight always marched it.
 *
 * @param o.eye    the director's eye {x, y, z}
 * @param o.yaw    sight bearing, yaw = atan2(-z, x)
 * @param o.pitch  sight elevation (rad)
 * @param o.maxR   the main battery's reach
 * @param o.field  the terrain field (surfaceAt)
 * @param o.hulls  enemy hulls from sightHull(); o.nHulls of them are live
 * @param o.waterLevelM  the sea surface
 * @param o.peakY  nothing stands above this: a climbing ray past it is sky
 */
export function navalSightRange(o) {
  const { eye, field } = o;
  const maxR = o.maxR ?? 0;
  const wl = o.waterLevelM ?? 0;
  const ch = Math.cos(o.pitch);
  const dx = Math.cos(o.yaw) * ch;
  const dy = Math.sin(o.pitch);
  const dz = -Math.sin(o.yaw) * ch;

  let tHit = Infinity;
  let yHit = wl;
  const hulls = o.hulls ?? [];
  const n = o.nHulls ?? hulls.length;
  let best = Infinity;
  for (let i = 0; i < n; i++) {
    const h = hulls[i];
    const px = h.x - eye.x;
    const py = h.y - eye.y;
    const pz = h.z - eye.z;
    const t = px * dx + py * dy + pz * dz; // along the ray to the closest approach
    if (t <= 0) continue;
    const ex = px - dx * t;
    const ey = py - dy * t;
    const ez = pz - dz * t;
    const miss2 = ex * ex + ey * ey + ez * ez;
    let score;
    if (h.naval) {
      if (miss2 > h.r * h.r) continue;
      score = 0;
    } else {
      const miss = Math.sqrt(miss2);
      if (miss > h.r + Math.max(SNAP_FLOOR_M, SNAP_MRAD * t)) continue;
      score = pierces(h, eye, dx, dy, dz) ? 0 : miss / t;
    }
    if (score < best || (score === best && t < tHit)) {
      best = score;
      tHit = t;
      yHit = h.y;
    }
  }

  // Where the sight meets the sea, if that comes first.
  const tWater = dy < -1e-4 ? (eye.y - wl) / -dy : Infinity;
  if (tWater < tHit) {
    tHit = tWater;
    yHit = wl;
  }
  // An island (or the shore) in the line of sight ranges CLOSER than
  // whatever it hides.
  const tCap = maxR > 0 ? maxR / Math.max(ch, 0.5) : 14000;
  const tLim = Math.min(tHit, tCap);
  const peak = o.peakY ?? Infinity;
  for (let d = NAVAL_MARCH_START_M; d < tLim; d += NAVAL_MARCH_M) {
    const rayY = eye.y + dy * d;
    if (dy >= 0 && rayY > peak) break; // climbed above any terrain
    const s = field.surfaceAt(eye.x + dx * d, eye.z + dz * d);
    if (s > rayY) {
      tHit = d;
      yHit = s;
      break;
    }
  }

  let range = tHit * ch;
  let max = false;
  // Small tolerance: the scope's range dial derives its pitch from the cap itself.
  if (maxR > 0 && range >= maxR - 2) {
    max = true;
    range = Math.min(range, maxR);
  }
  const ok = Number.isFinite(range);
  return { range: ok ? range : 0, max, y: ok && !max ? yHit : wl };
}

/**
 * The binocular gunsight's range dial: the sight pitch that lays the
 * crosshair on the surface `rangeM` out along the bearing, for a director's
 * eye at `eye`. At sea that is the water, as the dial always walked it. With
 * `shore` (the Brick Gulf) it walks up onto the land too, so a tank on the
 * farmland dials in the way a hull on the water does. Taken at the
 * sea, the dial topped out under an Abrams on 18 m of farmland 7.6 km off:
 * at the battery's full reach the crosshair still sat on the ground in front
 * of it.
 */
export function navalDialPitch(eye, yaw, rangeM, field, waterLevelM, shore) {
  let y = waterLevelM;
  if (shore) y = Math.max(y, field.surfaceAt(eye.x + Math.cos(yaw) * rangeM, eye.z - Math.sin(yaw) * rangeM));
  return -Math.atan((eye.y - y) / rangeM);
}

/**
 * Barrel elevation (rad) that lands a shell at horizontal range R for muzzle
 * speed v0: the low root of R = (v0²/g)·sin(2θ), 45° past the gun's reach.
 * Mirrors elevationForRange in internal/game/navalcombat.go, the lift a
 * warship's battery takes over its director's lay (a land gun takes none:
 * its gunner holds over, see landRoundAt).
 */
export function superelevation(rangeM, muzzleMS) {
  if (!(muzzleMS > 0) || rangeM <= 0) return 0;
  const s = (GRAVITY * rangeM) / (muzzleMS * muzzleMS);
  return s >= 1 ? Math.PI / 4 : 0.5 * Math.asin(s);
}

/**
 * The world point a land hull's chase camera looks at: the ranged point on
 * the sight line — the spot the gunner has laid on. Because the camera looks
 * THROUGH it from wherever the boom sits (lerping, clamped up off a
 * hillside), the crosshair at screen centre always lies on the sight line
 * at the ranged point: lay it on a hull and the sight ray lays on that hull
 * too. The old fixed 400 m aim left the crosshair 4-5 mrad off the sight line
 * beyond it (high) and inside it (low).
 *
 * Pass 0 for the old 400 m stand-in. main.js does for a crew with no cannon
 * laid on the sky: nothing up there but aircraft, and an Sd.Kfz. 7/1 hosing
 * down a strike plane is served better by a crosshair true at flak range than
 * at the far end of its reach. A cannon laid on the sky keeps looking through
 * the MAX point, so its crosshair stays on the sight line the rangefinder is
 * reading; a 400 m look from the boom would put the crosshair on dirt 600 m
 * out while the sight read sky. The range marks don't lean on any of this:
 * each is a world point of the round's own path, projected wherever the
 * camera looks.
 */
export function landChaseLook(eye, yaw, pitch, rangeM, out = {}) {
  const ch = Math.cos(pitch);
  const along = rangeM > 0 ? rangeM / Math.max(ch, 0.2) : 400;
  out.x = eye.x + Math.cos(yaw) * ch * along;
  out.y = eye.y + Math.sin(pitch) * along;
  out.z = eye.z - Math.sin(yaw) * ch * along;
  return out;
}

// A land round's birth: the mount rides landMountY over the hull's anchor
// (the anchor itself a metre over the ground), and the round leaves this far
// out along the bore. Mirrors fireLandShell; the autocannon's bullet is born
// half a metre farther out (fireAAGunAt), which no mark can show.
export const LAND_MOUNT_Y = 2.2;
export const LAND_MUZZLE_OUT_M = 4.5;

/**
 * Where a land hull's round is once it has flown `rangeM` metres (horizontal)
 * from the hull's anchor, fired down a lay of (yaw, pitch) at `muzzleMS`:
 * out of the muzzle along the bore, gravity the only force, exactly as the
 * server flies it. Nothing lifts the bore (fireLandShell), so this is where
 * the range mark for `rangeM` hangs: drawn there, a hull sat on the mark for
 * her range is a hull the round strikes, in the gunsight or the chase view.
 * Writes {x, y, z, t} (t: seconds of flight) into `out`.
 */
export function landRoundAt(anchor, yaw, pitch, muzzleMS, rangeM, out = {}) {
  const ch = Math.cos(pitch);
  const sh = Math.sin(pitch);
  const t = Math.max(0, rangeM - LAND_MUZZLE_OUT_M * ch) / (muzzleMS * Math.max(ch, 0.05));
  out.x = anchor.x + Math.cos(yaw) * rangeM;
  out.z = anchor.z - Math.sin(yaw) * rangeM;
  out.y = anchor.y + LAND_MOUNT_Y + LAND_MUZZLE_OUT_M * sh + muzzleMS * sh * t - 0.5 * GRAVITY * t * t;
  out.t = t;
  return out;
}

/**
 * The ranges a gun's holdover ladder marks, metres: every 500 m out to the
 * gun's reach, the way a tank sight's range scale reads (labelled in hundreds:
 * 5, 10, 15...). A slow round (the MUTT's 500 m/s recoilless, a metre of drop
 * at 250 m) gets a 250 m mark too, where the drop already carries it off a
 * hull. Nothing past four kilometres: no land sightline is longer.
 */
export function holdoverRanges(muzzleMS, reachM) {
  const out = [];
  if (!(muzzleMS > 0)) return out;
  const reach = Math.min(reachM > 0 ? reachM : 4000, 4000);
  if (muzzleMS < 800) out.push(250);
  for (let r = 500; r <= reach + 1e-6; r += 500) out.push(r);
  return out;
}

// The naval salvo's ellipse is narrower in elevation than across the line of
// fire: its vertical half-angle is this share of the horizontal one. Mirrors
// navalDispVertFrac in internal/game/navalcombat.go.
export const NAVAL_DISP_VERT_FRAC = 0.6;

// The spacing of a turret's barrels: they fire parallel, so a three-gun
// turret's rounds land this far either side of each other before any
// scatter. Mirrors navalTubeGapM in internal/game/navalcombat.go.
export const NAVAL_TUBE_GAP_M = 2.4;

/**
 * The warship gunsight's ring (main.js, the projected gunsight): the ellipse
 * a laid salvo comes down in, painted on the water at the ranged point, as
 * half-axes in metres. A salvo is laid to cross that point at mid-freeboard
 * (the server's navalAimY), the height that strikes the side of a ship laid
 * on; over open sea the rounds fly on and splash a little beyond, the way a
 * salvo that clears its target lands "over". The ring mirrors the server's
 * battery pattern at that crossing (navalPattern in
 * internal/game/navalcombat.go, an ellipse itself), so every round of a
 * perfect lay crosses inside it:
 *
 *   * across the line of fire, the battery's own half-angle out at the range,
 *     plus the spread of the widest turret's parallel barrels;
 *   * along it, how far long or short a round lands when it leaves the lay
 *     by the ellipse's vertical half-angle. On a flat naval arc every tenth
 *     of a mil of elevation is 14-17 m of range, as much at 2 km as at 12,
 *     so the ring is long and slim.
 *
 * The old ring sized itself off the salvo's across-the-line spread alone and
 * capped the long axis at 170 m, while the battery's 10-18 mil dice threw a
 * perfect lay across 1.3 km of sea: a ring you laid on a ship, around a
 * salvo that mostly fell somewhere else.
 *
 * @param dispersionRad  the battery's horizontal half-angle (mainGun.dispersionRad)
 * @param rangeM         the ranged distance, metres
 * @param muzzleMS       the battery's muzzle speed
 * @param barrels        the widest turret's barrels (they land side by side)
 */
export function navalRing(dispersionRad, rangeM, muzzleMS, barrels = 1) {
  const disp = dispersionRad > 0 ? dispersionRad : 0;
  if (!(muzzleMS > 0) || !(rangeM > 0)) return { along: 0, across: 0 };
  // dR/dθ of the flat throw R = v²·sin(2θ)/g, at the lay's own elevation.
  const el = superelevation(rangeM, muzzleMS);
  const dRange = (2 * muzzleMS * muzzleMS * Math.cos(2 * el)) / GRAVITY;
  const spread = ((Math.max(1, barrels) - 1) / 2) * NAVAL_TUBE_GAP_M;
  return { along: NAVAL_DISP_VERT_FRAC * disp * dRange, across: disp * rangeM + spread };
}

/** The most barrels one of a battery's mounts fires side by side. */
export function navalWidestMount(mg) {
  const turrets = mg?.turrets ?? [];
  if (!turrets.length) return Math.max(1, mg?.tubes ?? 1);
  return Math.max(1, ...turrets.map((t) => t.barrels ?? 1));
}

/**
 * The ring the projected gunsight paints for a hull crew (main.js), as the
 * half-axes it is drawn at, in metres: {along, across} the line of fire. At
 * sea it is the salvo's own fall of shot (navalRing), never slimmer than a
 * mark you can see; a land crew's, and every crew's in the Gulf, keeps the
 * honest flat-fire danger space, where a round that falls short still kills
 * along the corridor the ring shows. Its near edge (the range less `along`)
 * is where the island check stops looking: land past it is where the salvo
 * was laid to fall.
 *
 * @param atSea  the naval theater
 * @param mg     the crew's gun ({dispersionRad, muzzleSpeedMS, turrets})
 * @param dist   the ranged distance, metres
 */
export function hullRingAxes(atSea, mg, dist) {
  const disp = mg.dispersionRad > 0 ? mg.dispersionRad : 0.008;
  const v = mg.muzzleSpeedMS ?? 800;
  if (atSea) {
    const ring = navalRing(disp, dist, v, navalWidestMount(mg));
    return { along: Math.max(12, ring.along), across: Math.max(8, ring.across) };
  }
  const elev = superelevation(dist, v);
  return {
    along: Math.min(dist * 0.3, Math.max(16, dist * 0.012, (disp * 0.6 * dist) / Math.max(Math.tan(elev), 0.03))),
    across: Math.max(10, dist * 0.005, disp * dist),
  };
}

// Default train limit either side of a mount's facing: 150° leaves a 60°
// wedge over the opposite end of the ship where the superstructure masks the
// guns. Mirrors navalTurretArcDeg in internal/game/navalcombat.go.
export const SHIP_TURRET_ARC_DEG = 150;

/** Whether a turret can train onto a world aim bearing given the hull heading —
 * the client mirror of turretBears() in internal/game/navalcombat.go. */
export function shipTurretBears(tc, hullYaw, aimYaw) {
  const arc = ((tc.arcDeg > 0 ? tc.arcDeg : SHIP_TURRET_ARC_DEG) * Math.PI) / 180;
  let rel = aimYaw - hullYaw - (tc.aft ? Math.PI : 0);
  rel = Math.atan2(Math.sin(rel), Math.cos(rel));
  return Math.abs(rel) <= arc;
}

/**
 * navalLayAim (navalcombat.go), one turret: the bearing and elevation that
 * carry a round from this mount's own trunnions onto the battery's ranged
 * point, `rangeM` from her pivot along the train `aimYaw`, coming down at
 * mid-freeboard or on the mark (`markY` over the waterline) when it stands
 * higher. Solved twice, the second time from where the first lay puts the
 * muzzle. A big ship's turrets stand a hundred metres and more apart, so
 * laid on one point they toe in: an Iowa's forward and after turrets part by
 * about two degrees at 2 km and four at 800 m. The sight flies each round
 * off this lay, and the renderer trains each turret on it, so the barrels you
 * see converge where the salvo falls. null when there's no lay to solve (no
 * range, no muzzle speed, a mark inside the barrel): the battery's parallel
 * aim stands, as the server keeps it.
 *
 * @param hull   {x, z, yaw}: her pivot on the water and heading
 * @param fwdM   the mount's keel offset from the pivot (negative = abaft)
 * @param mountY its trunnions' height over the waterline
 * @param barrel trunnion-to-muzzle reach
 */
export function navalTurretLay(hull, fwdM, mountY, barrel, aimYaw, rangeM, markY, v0) {
  if (!(rangeM > 0) || !(v0 > 0)) return null;
  const bx = hull.x + Math.cos(hull.yaw) * fwdM;
  const bz = hull.z - Math.sin(hull.yaw) * fwdM;
  const dx = hull.x + Math.cos(aimYaw) * rangeM - bx;
  const dz = hull.z - Math.sin(aimYaw) * rangeM - bz;
  const r = Math.hypot(dx, dz);
  if (r <= barrel + 1) return null;
  const aimY = Math.max(NAVAL_AIM_Y, markY ?? 0);
  let pitch = elevationToHeight(r - barrel, mountY - aimY, v0);
  pitch = elevationToHeight(r - barrel * Math.cos(pitch), mountY + barrel * Math.sin(pitch) - aimY, v0);
  return { yaw: Math.atan2(-dz, dx), pitch: Math.min(GUN_PITCH_MAX, Math.max(GUN_PITCH_MIN, pitch)) };
}

/**
 * Where one main-battery turret stands: {mountY, barrel}, its trunnions'
 * height over the waterline and its reach, from its config or the class's
 * legacy mount — the client mirror of navalMount (navalcombat.go).
 */
export function navalMountOf(type, tc, mg) {
  const dims = NAVAL_MOUNT_DIMS[type] ?? NAVAL_MOUNT_DIMS[0];
  return { mountY: tc?.mountY > 0 ? tc.mountY : dims[1], barrel: mg?.barrelM > 0 ? mg.barrelM : dims[2] };
}

/**
 * The range a battery's elevation was laid for: superelevation turned
 * around, R = (v0²/g)·sin(2θ). The server lays a captain's battery at
 * elevationForRange of the sight's range and an AI director's on its
 * near-flat solve to the lead, and puts only that elevation on the wire, so
 * this is how far out a watcher's client finds the point every turret
 * converges on. 0 for a battery laid level or below it (no lay).
 */
export function batteryLayRange(pitch, v0) {
  if (!(pitch > 0) || !(v0 > 0)) return 0;
  return ((v0 * v0) / GRAVITY) * Math.sin(2 * Math.min(pitch, Math.PI / 4));
}

// ---- The battery's own line of fire (navalSalvoMask) ----
//
// The director ranges from the masthead, but a salvo leaves from the turrets,
// ten to twenty metres lower, on an arc that barely climbs in its first
// kilometre. So a hill 400-900 m off the bow that the director's eye clears
// can still take the whole broadside: the server bursts a round on the first
// ground it meets (stepShells). In one live run an Iowa laid on an
// enemy 5.9 km out put three full salvos into a 45 m island 600 m off while
// the sight showed a clear line and the ring sat on the target. The AI's
// directors already hold fire behind an island (navalGunsMasked); a captain
// got no cue at all, and read the three reloads as bad luck.
//
// So the sight flies each bearing turret's round the server's way before the
// trigger does: the turret's own lay onto the ranged point (navalLayAim), out
// of its own muzzle, stepped at the server's tick with gravity its only
// force, and tested against the ground exactly as stepShells tests it. A
// round that meets land short of the ring's near edge is MASKED: the ring
// promises a fall of shot the island will take instead. The fixture
// client/test/fixtures/naval_mask.json holds this to rounds the real server
// fired past real islands (TestNavalMaskFixture).

// navalAimY (navalcombat.go): a laid salvo comes down at mid-freeboard over
// the ranged point, unless the mark stands higher (a tank up on the shore).
export const NAVAL_AIM_Y = 5;
// The server's elevation stops (aaa.go gunPitchMin / gunPitchMax).
const GUN_PITCH_MIN = -0.1;
const GUN_PITCH_MAX = 1.45;
// A class's legacy mount, for a turret config that gives no height or barrel
// of its own: [keel offset, trunnion height, barrel]. Mirrors navalMountDims.
const NAVAL_MOUNT_DIMS = [
  [20, 4.6, 9],
  [26, 5.0, 11],
  [40, 6.2, 19],
  [30, 5.0, 12],
];
// stepShells walks a naval round's tick in samples this far apart, on top of
// the endpoint (shellSegmentBuried: finer than a terrain cell, so a thin
// crest can't slip between two samples).
const NAVAL_SEG_M = 4;
// In the Brick Gulf a warship's round flies as a land round (Shell.Land):
// landShellStep walks its tick in 2 m steps, at most 40, and a town house
// stops it as surely as a hill does.
const LAND_SEG_M = 2;
const LAND_SEG_MAX = 40;
// A round still in the air after this long has flown past any ring.
const MASK_FLIGHT_SEC = 30;

// Most of a long lay flies high over open sea, where nothing can take it, and
// walking the server's four samples a tick all the way out cost a millisecond
// an Iowa's lay. So the march first asks the ground how tall it stands:
// each tile of it keeps its highest grid corner (the ground is triangles
// strung between the corners, so nothing in the tile stands taller, and the
// sea floors it), and a tick whose round stays above every tile it crosses
// is clear without a single sample. Only where a round comes down within
// reach of an island does the walk run. Exact, not a guess.
//
// In the Gulf a house can stand on the ground too, but only in a town: a
// tile within a town's reach stands a house taller, and the farmland between
// the towns doesn't. (With every tile a house taller, a shore bombardment's
// low arcs over the fields walked every sample for kilometres: 15 ms a lay.)
const MASK_TILE_CELLS = 4; // 48 m of ground a tile, on the 12 m grid
const MASK_TILES_MAX = 16384; // cached tiles per field; the line of fire moves slowly
// A town house on the ground, in the Gulf (townBuilding in terrain_core.js
// builds them up to 14 m, the church to 12; the sights allow 20).
const MASK_HOUSE_M = 20;
const tileTops = new WeakMap(); // field -> Map(tile key -> [its ground's top, with any house on it])

// The top of tile (tx, tz): its highest grid corner, floored at the sea
// (surfaceAt's triangles only interpolate their corners), and with `houses`
// a house taller where the tile reaches into a town (landBuildingAt looks no
// farther than a town's radius and a building lot past it). Tile tx holds
// the cells whose index floor(x / cellSize) falls in [tx·4, tx·4 + 4), and
// their corners out to +4.
function tileTop(field, tx, tz, houses) {
  let tiles = tileTops.get(field);
  if (!tiles) tileTops.set(field, (tiles = new Map()));
  const key = (tx + 32768) * 65536 + (tz + 32768);
  let top = tiles.get(key);
  if (top === undefined) {
    let ground = field.waterLevel ?? 0;
    const i0 = tx * MASK_TILE_CELLS;
    const j0 = tz * MASK_TILE_CELLS;
    for (let j = 0; j <= MASK_TILE_CELLS; j++) {
      for (let i = 0; i <= MASK_TILE_CELLS; i++) {
        const h = field.cornerHeight(i0 + i, j0 + j);
        if (h > ground) ground = h;
      }
    }
    const span = MASK_TILE_CELLS * field.cellSize;
    const x0 = tx * span;
    const z0 = tz * span;
    // A field that can't list its towns might build a house anywhere.
    const towns = field.townPlan ? field.townPlan() : null;
    const town =
      !towns ||
      towns.some((t) => {
        const dx = Math.max(x0 - t.x, 0, t.x - (x0 + span));
        const dz = Math.max(z0 - t.z, 0, t.z - (z0 + span));
        const reach = t.r + LAND_BUILDING_GRID_M;
        return dx * dx + dz * dz <= reach * reach;
      });
    top = [ground, town ? ground + MASK_HOUSE_M : ground];
    if (tiles.size >= MASK_TILES_MAX) tiles.delete(tiles.keys().next().value);
    tiles.set(key, top);
  }
  return houses ? top[1] : top[0];
}

// The tallest thing on the ground under one tick's flight: the highest tile
// top across the flight's footprint (a tick flies less than a tile, so it
// spans at most two a side). The tile of x is taken off the very cell index
// groundAt uses, so a point on a tile's edge can't fall between them.
function groundTopUnder(field, x0, z0, x1, z1, houses) {
  const cs = field.cellSize;
  const tile = (v) => Math.floor(Math.floor(v / cs) / MASK_TILE_CELLS);
  const ta = tile(Math.min(x0, x1));
  const tb = tile(Math.max(x0, x1));
  const ua = tile(Math.min(z0, z1));
  const ub = tile(Math.max(z0, z1));
  let top = -Infinity;
  for (let tx = ta; tx <= tb; tx++) {
    for (let tz = ua; tz <= ub; tz++) top = Math.max(top, tileTop(field, tx, tz, houses));
  }
  return top;
}

/**
 * elevationToHeight (navalcombat.go): the low lay that drops a round from a
 * muzzle `dh` metres over the mark onto it at `rangeM`, the low root of
 * dh + R·tanθ − (gR²/2v²)(1+tan²θ) = 0; 45° past the gun's reach.
 */
export function elevationToHeight(rangeM, dh, muzzleMS) {
  if (!(muzzleMS > 0) || !(rangeM > 0)) return 0;
  const k = (GRAVITY * rangeM * rangeM) / (2 * muzzleMS * muzzleMS);
  const disc = rangeM * rangeM - 4 * k * (k - dh);
  if (disc < 0) return Math.PI / 4;
  return Math.atan((rangeM - Math.sqrt(disc)) / (2 * k));
}

// The first point of one tick's flight (p0 → p1) that the server buries in
// the ground: stepShells' naval test (the endpoint, then the interior walk),
// or in the Gulf landShellStep's (houses first, then the ground, step by
// step). Returns the burst point, surfaced where the server surfaces it, or
// null when the tick flies clear.
function segmentBurst(field, land, x0, y0, z0, x1, y1, z1) {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dz = z1 - z0;
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (land) {
    const steps = Math.min(Math.floor(len / LAND_SEG_M) + 1, LAND_SEG_MAX);
    for (let i = 1; i <= steps; i++) {
      const f = i / steps;
      const x = x0 + dx * f;
      const y = y0 + dy * f;
      const z = z0 + dz * f;
      const s = field.surfaceAt(x, z);
      // Only a round down among the rooftops can meet a house (landShellStep
      // asks at every step; a round higher than any house needn't).
      const b = y <= s + MASK_HOUSE_M && field.landBuildingAt ? field.landBuildingAt(x, z) : null;
      if (b && y <= s + b.h) return { x, y, z };
      if (y <= s) return { x, y: s, z };
    }
    return null;
  }
  const s1 = field.surfaceAt(x1, z1);
  if (y1 <= s1) return { x: x1, y: s1, z: z1 };
  const n = Math.floor(len / NAVAL_SEG_M);
  for (let i = 1; i <= n; i++) {
    const f = i / (n + 1);
    const x = x0 + dx * f;
    const z = z0 + dz * f;
    const s = field.surfaceAt(x, z);
    if (y0 + dy * f <= s) return { x, y: s, z };
  }
  return null;
}

// erf, to seven places (Abramowitz & Stegun 7.1.26): the normal law's area.
function erf(x) {
  const s = x < 0 ? -1 : 1;
  const a = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * a);
  const p = ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t + 0.254829592) * t;
  return s * (1 - p * Math.exp(-a * a));
}

/**
 * The share of a battery's pattern whose elevation scatter falls below `gy`
 * of its standard deviations. navalPattern draws (gx, gy) standard normal and
 * keeps the pair inside the disc of radius `sigma`, so gy alone is the normal
 * law thinned toward the rim, where the disc narrows: its density goes as
 * e^(−gy²/2)·erf(√((sigma² − gy²)/2)), the same either side of the lay.
 * Integrated by Simpson's rule out from the lay, so the lay splits it in
 * half exactly.
 */
export function patternBelow(gy, sigma) {
  if (gy <= -sigma) return 0;
  if (gy >= sigma) return 1;
  const f = (y) => Math.exp(-0.5 * y * y) * erf(Math.sqrt(Math.max(0, sigma * sigma - y * y) / 2));
  const area = (b) => {
    const n = 32;
    const h = b / n;
    let acc = f(0) + f(b);
    for (let i = 1; i < n; i++) acc += f(i * h) * (i % 2 ? 4 : 2);
    return (acc * h) / 3;
  };
  return 0.5 + (gy < 0 ? -0.5 : 0.5) * (area(Math.abs(gy)) / area(sigma));
}

// Halvings that find where a partly masked salvo splits, from the lay out to
// the edge of the pattern on the side that splits: three pin it to a
// sixteenth of the pattern's height, well inside a round of a broadside.
const MASK_SPLIT_STEPS = 3;

/**
 * Does land take the salvo short of the ring? Flies each turret that bears
 * on the lay the way the server fires it (fireNavalShell → navalLayAim →
 * stepShells) and reports how much of the salvo bursts short of the ring's
 * near edge.
 *
 * The battery's pattern scatters each round's elevation (navalPattern), so
 * a turret flies the pattern's highest round and its lowest as well as its
 * lay. A rock off the bow takes the highest round, and with it the whole
 * pattern. Open water under the lowest round means nothing in the pattern
 * meets land. Between those, the salvo splits: a low island just short of
 * the ring takes the low rounds while the high ones clear its crest and
 * splash long, so the flight is halved down to where the split falls and
 * the pattern's share below it is what the island takes. (Counted off the
 * lays alone, a crest just short of an Iowa's 5.8 km lay read all nine
 * rounds masked in the playtest while six of them burst on it.)
 *
 * Returns {bearing, total, masked, every, at, rounds}: the barrels bearing
 * and the battery's total (batteryBarrels); how many of the bearing barrels'
 * rounds land takes, to the nearest round, never none when any is; whether
 * it takes every one of them (even the pattern's highest round); the nearest
 * burst on land (null when none); and each turret's round laid dead on, in
 * config order: where it bursts short of the ring, or null (clear, or the
 * turret doesn't bear). null when the hull has no turret layout or no lay:
 * the sight can't say.
 *
 * @param o.hull    {x, y, z, yaw}: her pivot on the water (y = the waterline) and heading
 * @param o.type    her hull class (only a turret config without its own mount numbers needs it)
 * @param o.mg      her mainGun config
 * @param o.aimYaw  the battery's train: the sight's bearing, yaw = atan2(-z, x)
 * @param o.rangeM  the lay's range from her pivot (the sight's, clamped to the reach)
 * @param o.markY   the mark's height over the waterline (navalMarkHeight): 0 at sea
 * @param o.nearM   the ring's near edge along the line of fire from her pivot:
 *                  land past it is where the salvo was laid to fall anyway
 * @param o.field   the terrain field (surfaceAt; landBuildingAt in the Gulf).
 *                  A field that can't hand over its grid corners (cornerHeight,
 *                  cellSize) is walked sample by sample all the way out.
 * @param o.land    the Brick Gulf, where her rounds fly as land rounds
 * @param o.dt      the server's tick (1 / net.tickRateHz)
 */
export function navalSalvoMask(o) {
  const { hull, mg, field } = o;
  const turrets = mg?.turrets ?? [];
  const v0 = mg?.muzzleSpeedMS ?? 0;
  const R = o.rangeM;
  if (!turrets.length || !(v0 > 0) || !(R > 0)) return null;
  const dt = o.dt > 0 ? o.dt : 1 / 60;
  const tiled = typeof field.cornerHeight === "function" && field.cellSize > 0;
  const nearM = o.nearM ?? R;
  const { barrel } = navalMountOf(o.type, null, mg);
  const markY = Math.max(NAVAL_AIM_Y, o.markY ?? 0);
  // Her heading and the line of fire, as unit vectors on the sea.
  const hx = Math.cos(hull.yaw);
  const hz = -Math.sin(hull.yaw);
  const ax = Math.cos(o.aimYaw);
  const az = -Math.sin(o.aimYaw);
  const along = (x, z) => (x - hull.x) * ax + (z - hull.z) * az;
  // One round out of the muzzle (px, py, pz) along (yaw, pitch), tick by
  // tick the way stepShells flies it (the velocity takes the tick's gravity
  // first, then carries the round), to whatever takes it short of the ring's
  // near edge; null once it flies past.
  const fly = (px, py, pz, yaw, pitch) => {
    const ch = Math.cos(pitch);
    const vx = Math.cos(yaw) * ch * v0;
    let vy = Math.sin(pitch) * v0;
    const vz = -Math.sin(yaw) * ch * v0;
    for (let t = 0; t < MASK_FLIGHT_SEC && along(px, pz) < nearM; t += dt) {
      vy -= GRAVITY * dt;
      const nx = px + vx * dt;
      const ny = py + vy * dt;
      const nz = pz + vz * dt;
      if (!tiled || Math.min(py, ny) <= groundTopUnder(field, px, pz, nx, nz, o.land)) {
        const b = segmentBurst(field, o.land, px, py, pz, nx, ny, nz);
        if (b) return along(b.x, b.z) < nearM ? b : null;
      }
      px = nx;
      py = ny;
      pz = nz;
    }
    return null;
  };
  // The pattern's elevation scatter (navalPattern): gy/sigma of its vertical
  // half-angle, gy standard normal inside the disc of radius sigma.
  const sigma = mg.sigma > 0 ? mg.sigma : 2;
  const half = (mg.dispersionRad > 0 ? mg.dispersionRad : 0) * NAVAL_DISP_VERT_FRAC;
  const out = { bearing: 0, total: 0, masked: 0, every: false, at: null, rounds: [] };
  let atAlong = Infinity;
  const mark = (hit) => {
    const a = hit ? along(hit.x, hit.z) : Infinity;
    if (a < atAlong) {
      atAlong = a;
      out.at = hit;
    }
  };
  let taken = 0; // barrels' worth of rounds land takes
  let every = true; // every round of every bearing turret taken
  for (const tc of turrets) {
    const n = Math.max(1, tc.barrels ?? 1);
    out.total += n;
    if (!shipTurretBears(tc, hull.yaw, o.aimYaw)) {
      out.rounds.push(null);
      continue;
    }
    out.bearing += n;
    // navalLayAim: this turret's own bearing and elevation from its own
    // trunnions onto the ranged point.
    const fwdM = tc.fwdM ?? 0;
    const { mountY } = navalMountOf(o.type, tc, mg);
    const bx = hull.x + hx * fwdM;
    const bz = hull.z + hz * fwdM;
    const lay = navalTurretLay(hull, fwdM, mountY, barrel, o.aimYaw, R, markY, v0);
    const yaw = lay ? lay.yaw : o.aimYaw;
    const pitch = lay ? lay.pitch : Math.min(GUN_PITCH_MAX, Math.max(GUN_PITCH_MIN, superelevation(R, v0)));
    // Every round leaves the barrel tip on the lay (navalTurretMuzzle); the
    // pattern only turns its flight.
    const ch = Math.cos(pitch);
    const px = bx + Math.cos(yaw) * ch * barrel;
    const py = hull.y + mountY + Math.sin(pitch) * barrel;
    const pz = bz - Math.sin(yaw) * ch * barrel;
    const dead = fly(px, py, pz, yaw, pitch);
    out.rounds.push(dead);
    mark(dead);
    // The share of this turret's pattern land takes.
    let share = dead ? 1 : 0;
    if (half > 0) {
      const high = fly(px, py, pz, yaw, pitch + half);
      mark(high);
      if (high) share = 1; // even the highest round meets land
      else {
        // The pattern splits somewhere below its highest round. Taken dead
        // on the lay, every round laid lower is taken too: the split lies
        // above the lay. Clear dead on it, the split lies below, if the
        // lowest round is taken at all.
        const low = dead ? null : fly(px, py, pz, yaw, pitch - half);
        mark(low);
        if (dead || low) {
          // Halve the flight down to the split: a round laid `lo` off the
          // lay is taken, one laid `hi` off flies clear.
          let lo = dead ? 0 : -half;
          let hi = dead ? half : 0;
          for (let i = 0; i < MASK_SPLIT_STEPS; i++) {
            const mid = (lo + hi) / 2;
            if (fly(px, py, pz, yaw, pitch + mid)) lo = mid;
            else hi = mid;
          }
          share = patternBelow((((lo + hi) / 2) / half) * sigma, sigma);
        }
      }
    }
    taken += n * share;
    if (share < 1) every = false;
  }
  if (taken > 0) {
    out.every = every;
    out.masked = every ? out.bearing : Math.max(1, Math.min(Math.round(taken), out.bearing));
  }
  return out;
}
